/**
 * Map2DView - Google Maps 2D Implementation
 * RoofMapper v3 - NIKO Energy
 */

import React, { useEffect, useRef, useState } from 'react';
import { useMap } from '../../contexts/MapContext';
import { useSolar } from '../../contexts/SolarContext';

// Default center (Mexico City)
const DEFAULT_CENTER = {
  lat: 19.42241280654638,
  lng: -99.21513116670044,
};

interface Map2DViewProps {
  onMapReady?: (map: google.maps.Map) => void;
  drawingMode?: 'none' | 'measure' | 'polygon' | 'panel';
}

export default function Map2DView({ onMapReady, drawingMode = 'none' }: Map2DViewProps): JSX.Element | null {
  const { setPinPosition, pinPosition, cameraState, setCameraState, setMap2DRef } = useMap();
  const { buildingInsights } = useSolar();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const [isReady, setIsReady] = useState(false);

  // Initialize 2D map with Street View controls
  useEffect(() => {
    const initMap = async () => {
      try {
        if (!window.google?.maps) {
          console.error('Google Maps API not loaded');
          return;
        }

        if (!containerRef.current) {
          console.error('Container ref not ready');
          return;
        }

        const center = cameraState.center || DEFAULT_CENTER;

        // Create 2D map with Street View Pegman enabled.
        // NO usamos mapId a proposito: con mapId el mapa pasa a Vector Maps y
        // en Chrome/Edge sobre Windows el pinch del trackpad no zoomea bien.
        // El componente usa google.maps.Marker (legacy) asi que no hace falta.
        const map = new google.maps.Map(containerRef.current, {
          center: { lat: center.lat, lng: center.lng },
          zoom: cameraState.zoom || 19,
          mapTypeId: google.maps.MapTypeId.SATELLITE,
          tilt: 0, // Always 0 for 2D
          heading: cameraState.heading || 0,
          gestureHandling: 'greedy',
          scrollwheel: true,
          streetViewControl: true, // Enable Street View Pegman
          streetViewControlOptions: {
            position: google.maps.ControlPosition.RIGHT_BOTTOM,
          },
        });

        mapRef.current = map;
        setMap2DRef(map);

        // Listen for Street View visibility changes
        const streetView = map.getStreetView();
        streetView.addListener('visible_changed', () => {
          const isVisible = streetView.getVisible();
          
          if (isVisible) {
            // User opened Street View via Pegman - update context
            // Don't change mode - Street View is part of 2D experience
          }
        });

        // Listen for clicks
        map.addListener('click', (event: google.maps.MapMouseEvent) => {
          if (event.latLng) {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();

            if (drawingMode === 'none') {
              setPinPosition({ lat, lng });
            }
          }
        });

        // Listen for camera changes
        map.addListener('center_changed', () => {
          const center = map.getCenter();
          const zoom = map.getZoom();
          if (center) {
            setCameraState({
              center: {
                lat: center.lat(),
                lng: center.lng(),
                altitude: cameraState.center?.altitude || 0,
              },
              zoom: zoom || 19,
              heading: cameraState.heading || 0,
              tilt: 0,
              range: cameraState.range || 500,
            });
          }
        });

        setIsReady(true);
        if (onMapReady) onMapReady(map);

      } catch (error) {
        console.error('Error initializing 2D map:', error);
      }
    };

    const timeout = setTimeout(initMap, 100);
    return () => clearTimeout(timeout);
  }, []);

  // Place marker when pin position changes (ONLY in normal mode)
  useEffect(() => {
    if (!mapRef.current || !pinPosition || drawingMode !== 'none' || !isReady) {
      return;
    }

    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    // Create new draggable marker
    const marker = new google.maps.Marker({
      position: { lat: pinPosition.lat, lng: pinPosition.lng },
      map: mapRef.current,
      title: 'Selected Location',
      draggable: true, // Enable dragging in 2D
    });

    // Listen for drag end to update pin position
    marker.addListener('dragend', (event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        setPinPosition({ lat, lng });
      }
    });

    markerRef.current = marker;
  }, [pinPosition, drawingMode, isReady]);

  // Update camera when external changes occur
  useEffect(() => {
    if (!mapRef.current || !cameraState.center) return;

    const map = mapRef.current;
    const currentCenter = map.getCenter();
    
    // Only update if significantly different
    if (
      !currentCenter ||
      Math.abs(currentCenter.lat() - cameraState.center.lat) > 0.0001 ||
      Math.abs(currentCenter.lng() - cameraState.center.lng) > 0.0001
    ) {
      map.setCenter({ lat: cameraState.center.lat, lng: cameraState.center.lng });
    }

    if (cameraState.zoom && map.getZoom() !== cameraState.zoom) {
      map.setZoom(cameraState.zoom);
    }
  }, [cameraState]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
    };
  }, []);

  return (
    <div ref={containerRef} className="absolute inset-0" />
  );
}
